/**
 * Readable text colour for a coloured background (Monday 12896825330).
 *
 * Deliberately dependency-free, like `categoryVisibility.ts`. Every other
 * module under `common/helpers` pulls in `../env`, which reads
 * REACT_APP_COMMON_ENV at import time and throws without it, so anything
 * living there cannot be unit tested. A colour rule is cheap to get wrong and
 * invisible when it is, so it lives on its own and takes everything it needs
 * as arguments.
 *
 * WHY IT EXISTS
 * The Guideline badge printed white text on whatever colour its variant
 * carried. Measured across every variant the themes actually define, two of
 * them are unreadable:
 *
 *   warning, light   #CCCC00   1.72:1   <- what Jaron photographed
 *   info, Nirvana    #b8a843   2.41:1
 *   danger           #EB0C00   4.58:1
 *   info, UPS        #0071EB   4.60:1
 *   info, Glow       #210102  19.63:1
 *   warning, dark    #440     10.13:1
 *
 * WCAG AA wants 4.5:1 for small text, and this badge is 9px.
 */

/** WCAG AA for small text. The badge's font size is 9. */
export const WCAG_AA_SMALL = 4.5

export const WHITE = '#FFFFFF'
export const BLACK = '#000000'

/** `#abc` and `#aabbcc`, with or without the hash. Anything else is not ours. */
const parseHex = (color: string): readonly [number, number, number] | null => {
  const hex = color.trim().replace(/^#/, '')
  const full =
    hex.length === 3
      ? hex
          .split('')
          .map((ch) => ch + ch)
          .join('')
      : hex

  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null

  return [
    Number.parseInt(full.slice(0, 2), 16),
    Number.parseInt(full.slice(2, 4), 16),
    Number.parseInt(full.slice(4, 6), 16),
  ]
}

const channel = (value: number): number => {
  const c = value / 255
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
}

/** WCAG relative luminance. Returns null for a colour this cannot parse. */
export const relativeLuminance = (color: string): number | null => {
  const rgb = parseHex(color)
  if (!rgb) return null

  const [r, g, b] = rgb
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

/**
 * WCAG contrast ratio, 1 (identical) to 21 (black on white). Returns null if
 * either colour cannot be parsed, so a caller can tell "unreadable" apart from
 * "unknown" instead of getting a number that means neither.
 */
export const contrastRatio = (a: string, b: string): number | null => {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  if (la === null || lb === null) return null

  const [hi, lo] = la > lb ? [la, lb] : [lb, la]
  return (hi + 0.05) / (lo + 0.05)
}

/**
 * White unless white is unreadable on this background, in which case black.
 *
 * NOT "whichever of the two scores higher". On `#EB0C00` black wins by 0.01
 * (4.59 against 4.58) and on `#0071EB` white wins by 0.04 - picking the
 * winner would flip the error badge to black text for a difference nobody can
 * see. Both of those already clear AA, so they keep the white they have and
 * only the variants that genuinely fail change.
 *
 * An unparseable colour keeps white, which is exactly what the badge did
 * before this existed - a theme this cannot read must not silently restyle.
 */
export const readableTextOn = (background: string): string => {
  const onWhite = contrastRatio(WHITE, background)
  if (onWhite === null) return WHITE

  return onWhite >= WCAG_AA_SMALL ? WHITE : BLACK
}
